import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
	Combobox,
	ComboboxChip,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInputGroup,
	ComboboxItem,
	ComboboxList,
	ComboboxValue,
} from "@/components/ui/combobox";
import { type Profile } from "@/services/profile.service";
import { getInitials, profileColorClass } from "./types";

/** Filter-only pseudo user for tasks with no assignee. */
export const UNASSIGNED_USER_ID = "unassigned";
const UNASSIGNED_OPTION: Profile = {
	id: UNASSIGNED_USER_ID,
	full_name: "Unassigned",
	email: "",
	avatar_url: null,
	role: "",
	status: "",
};

export function UserMultiSelect({
	profiles,
	value,
	onValueChange,
	onOpenChange,
	placeholder,
	disabled,
	autoFocus,
	defaultOpen,
	size = "default",
	includeUnassigned = false,
}: {
	profiles: Profile[];
	value: string[];
	onValueChange: (ids: string[]) => void;
	onOpenChange?: (open: boolean) => void;
	placeholder?: string;
	disabled?: boolean;
	autoFocus?: boolean;
	defaultOpen?: boolean;
	size?: "sm" | "default";
	includeUnassigned?: boolean;
}) {
	const items = includeUnassigned ? [UNASSIGNED_OPTION, ...profiles] : profiles;
	return (
		<Combobox
			items={items}
			multiple
			disabled={disabled}
			defaultOpen={defaultOpen}
			value={items.filter((p) => value.includes(p.id))}
			onValueChange={(selected: Profile[]) =>
				onValueChange(selected.map((p) => p.id))
			}
			onOpenChange={onOpenChange}
			itemToStringValue={(p: Profile) => p.full_name ?? p.email}
		>
			<ComboboxInputGroup>
				<ComboboxChips className={size === "sm" ? "min-h-8 text-xs" : "min-h-9 text-xs"}>
					<ComboboxValue>
						{(selected: Profile[]) =>
							selected.map((p) => (
								<ComboboxChip key={p.id}>
									{p.full_name ?? p.email}
								</ComboboxChip>
							))
						}
					</ComboboxValue>
					<ComboboxChipsInput
						autoFocus={autoFocus}
						placeholder={value.length === 0 ? placeholder : undefined}
					/>
				</ComboboxChips>
			</ComboboxInputGroup>
			<ComboboxContent>
				<ComboboxEmpty>No users found.</ComboboxEmpty>
				<ComboboxList>
					{(p: Profile) => (
						<ComboboxItem key={p.id} value={p}>
							<Avatar className="h-5 w-5">
								{p.avatar_url && (
									<AvatarImage
										src={p.avatar_url}
										alt={p.full_name ?? p.email}
									/>
								)}
								<AvatarFallback
									className={`text-[9px] text-white ${profileColorClass(p.id)}`}
								>
									{getInitials(p.full_name ?? p.email)}
								</AvatarFallback>
							</Avatar>
							<span className="truncate">{p.full_name ?? p.email}</span>
						</ComboboxItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
}
